import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/auth';
import { THEMES, THEME_LABELS } from '../theme';
import { API_URL } from '../config';
import AdminModal from './AdminModal';

const AdminBranchManager = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyForm = {
    name: '', code: '', theme: 'purple',
    display_name: '', facility_name: '', address_line1: '', address_line2: '',
    hours_text: '', hours_note: '', is_public: true,
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadBranches(); }, []);

  const loadBranches = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/branches`);
      const data = await res.json();
      if (res.ok) setBranches(data.branches || []);
      else setError(data.error || 'Failed to load branches');
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setError('');
  };

  const handleOpenEdit = (branch) => {
    setEditingId(branch.id);
    setForm({
      name: branch.name, code: branch.code, theme: branch.theme || 'purple',
      display_name: branch.display_name || '', facility_name: branch.facility_name || '',
      address_line1: branch.address_line1 || '', address_line2: branch.address_line2 || '',
      hours_text: branch.hours_text || '', hours_note: branch.hours_note || '',
      is_public: branch.is_public !== false,
    });
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.code.trim()) {
      setError('Name and code are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const url = editingId
        ? `${API_URL}/api/admin/branches/${editingId}`
        : `${API_URL}/api/admin/branches`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetchWithAuth(url, { method, body: JSON.stringify(form) });
      const data = await res.json();
      if (res.ok && data.success) {
        setShowForm(false);
        loadBranches();
      } else {
        setError(data.error || 'Save failed');
      }
    } catch {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete branch "${name}"? This will affect all users and data in this branch.`)) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/branches/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok && data.success) loadBranches();
      else setError(data.error || 'Delete failed');
    } catch {
      setError('Network error');
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '800px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Branch Management</h2>
        <button className="btn-primary" onClick={handleOpenCreate}>+ New Branch</button>
      </div>

      {error && !showForm && <div className="error-banner">{error}</div>}

      {showForm && (
        <AdminModal
          title={editingId ? 'Edit Branch' : 'Create Branch'}
          size="lg" onClose={closeForm} onSubmit={handleSave} saving={saving} error={error}
        >
          <div className="form-row">
            <label>Branch Name</label>
            <input
              type="text"
              value={form.name}
              autoFocus
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Central Texas"
            />
          </div>
          <div className="form-row">
            <label>Short Code</label>
            <input
              type="text"
              value={form.code}
              onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="e.g. CTTA"
              maxLength={10}
            />
            <small>Unique identifier (2–10 uppercase letters)</small>
          </div>
          <div className="form-row">
            <label>Color Theme</label>
            <select
              value={form.theme}
              onChange={e => setForm({ ...form, theme: e.target.value })}
            >
              {THEMES.map(t => (
                <option key={t} value={t}>{THEME_LABELS[t]}</option>
              ))}
            </select>
            <small>Default theme for this branch's public site and admin portal</small>
          </div>

          {editingId && (
            <>
              <h3 style={{ marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
                Public Location Page
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', marginTop: '-8px' }}>
                Shown on the public Location page. Leave blank and this branch won't show a card
                there yet. The map and directions link are built automatically from the address
                fields below -- there's nothing separate to keep in sync.
              </p>
              <div className="form-row">
                <label>Public display name</label>
                <input
                  type="text"
                  value={form.display_name}
                  onChange={e => setForm({ ...form, display_name: e.target.value })}
                  placeholder="e.g. Avery Ranch, Austin"
                />
              </div>
              <div className="form-row">
                <label>Facility name</label>
                <input
                  type="text"
                  value={form.facility_name}
                  onChange={e => setForm({ ...form, facility_name: e.target.value })}
                  placeholder="e.g. Great Oaks Elementary"
                />
              </div>
              <div className="form-row">
                <label>Street address</label>
                <input
                  type="text"
                  value={form.address_line1}
                  onChange={e => setForm({ ...form, address_line1: e.target.value })}
                  placeholder="e.g. 16455 Great Oaks Drive"
                />
              </div>
              <div className="form-row">
                <label>City, state, zip</label>
                <input
                  type="text"
                  value={form.address_line2}
                  onChange={e => setForm({ ...form, address_line2: e.target.value })}
                  placeholder="e.g. Round Rock, TX 78681"
                />
              </div>
              <div className="form-row">
                <label>Hours</label>
                <input
                  type="text"
                  value={form.hours_text}
                  onChange={e => setForm({ ...form, hours_text: e.target.value })}
                  placeholder="e.g. Saturday: 9:00am - 1:00pm"
                />
              </div>
              <div className="form-row">
                <label>Hours note</label>
                <input
                  type="text"
                  value={form.hours_note}
                  onChange={e => setForm({ ...form, hours_note: e.target.value })}
                  placeholder="e.g. (see the calendar for school days & batch times)"
                />
              </div>
              <div className="form-row" style={{ flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                <label style={{ margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={form.is_public}
                    onChange={e => setForm({ ...form, is_public: e.target.checked })}
                  />
                  {' '}Show this branch on the public site (Location page, Calendar picker, ...)
                </label>
              </div>
              <p style={{ fontSize: '13px', color: '#64748b', marginTop: '-8px' }}>
                Uncheck for an admin-only/test branch that shouldn't appear anywhere on the public site.
              </p>
            </>
          )}

        </AdminModal>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Code</th>
              <th>Theme</th>
              <th>Public?</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {branches.map(b => (
              <tr key={b.id}>
                <td>{b.id}</td>
                <td>{b.name}</td>
                <td><code>{b.code}</code></td>
                <td style={{ textTransform: 'capitalize' }}>{b.theme || 'purple'}</td>
                <td>{b.is_public === false ? 'Hidden' : 'Yes'}</td>
                <td>{b.created_at ? new Date(b.created_at).toLocaleDateString() : '—'}</td>
                <td>
                  <button className="btn-sm" onClick={() => handleOpenEdit(b)}>Edit</button>
                  {' '}
                  <button className="btn-sm btn-danger" onClick={() => handleDelete(b.id, b.name)}>Delete</button>
                </td>
              </tr>
            ))}
            {branches.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888' }}>No branches yet</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminBranchManager;
